import { ActionIcon, Button, Flex, Tabs } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { isNull } from "lodash";
import { useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useClikCities } from "../../api/hooks/clikData/useClikCities.ts";
import { useClikCountries } from "../../api/hooks/clikData/useClikCountries.ts";
import { useClikData } from "../../api/hooks/clikData/useClikData.ts";
import { useCompanyOptions } from "../../api/hooks/customer/useCompanyOptions.ts";
import { useCustomerChangeLog } from "../../api/hooks/customer/useCustomerChangeLog.ts";
import { useCustomerDirectorInfo } from "../../api/hooks/customer/useCustomerDirectorInfo.ts";
import { useFreezeCustomerStatus } from "../../api/hooks/customer/useFreezeCustomerStatus.ts";
import { useUnFreezeCustomerStatus } from "../../api/hooks/customer/useUnFreezeCustomerStatus.ts";
import SvgChevronLeft from "../../components/Icons/ChevronLeft.tsx";
import SvgSnowflake from "../../components/Icons/Snowflake.tsx";
import LateFeeTab from "../../components/LateFeeTab/LateFeeTab.tsx";
import Layout from "../../components/Layout/Layout.tsx";
import ModalWithNotes from "../../components/ModalWithNotes/ModalWithNotes.tsx";
import Title from "../../components/Title/Title.tsx";
import { AppRoute } from "../../constants.ts";
import { CustomerFreezeStatus } from "../../graphql/generated.ts";
import { useMutationNotificationWrapper } from "../../hooks/useMutationNotificationWrapper.tsx";
import { useTabFromUrl } from "../../hooks/useTabFromUrl.tsx";
import { useCurrentUserContext } from "../../providers/CurrentUserProvider.tsx";
import { useStyles } from "./ClikDataDetailsPage.styles.ts";
import ClikResultList from "./ClikResultList/ClikResultList.tsx";
import CompanyInfo from "./CompanyInfo/CompanyInfo.tsx";
import { getTabs, TabValue } from "./constants.tsx";
import { CustomerStatusLog } from "./CustomerStatusLog/CustomerStatusLog.tsx";
import InvoicesHistoryList from "./InvoicesHistory/InvoicesHistoryList.tsx";
import LimitInfo from "./LimitInfo/LimitInfo.tsx";
import PengurusInfo from "./PengurusInfo/PengurusInfo.tsx";
import TransactionHistoryList from "./TransactionHistory/TransactionHistoryList.tsx";

const ClikDataDetailsPage = () => {
  const { classes } = useStyles();

  const navigate = useNavigate();

  const { user } = useCurrentUserContext();

  const { id } = useParams() as { id: string };

  const [
    { data: clikDataResponse, fetching: clikDataFetching, error },
    refreshClikData,
  ] = useClikData({ id });
  const clikData = clikDataResponse?.clikData;
  const clikDataParentId = clikData?.parentId ?? clikData?.id;
  const customerId = clikData?.company?.id;

  const [{ data: clikCitiesData, fetching: clikCitiesFetching }] =
    useClikCities();

  const [{ data: clikCountriesData, fetching: clikCountriesFetching }] =
    useClikCountries();

  const [{ data: companiesData, fetching: companiesFetching }] =
    useCompanyOptions();

  const [
    { data: customerDirectorInfoData, fetching: fetchingCustomerDirectorInfo },
  ] = useCustomerDirectorInfo({ customerId });
  const customerDirectorInfo = customerDirectorInfoData?.customerDirectorInfo;

  const [
    { data: customerChangeLogData, fetching: customerChangeLogFetching },
    refreshCustomerChangeLog,
  ] = useCustomerChangeLog({
    id: customerId,
  });
  const customerChangeLog = customerChangeLogData?.customerChangeLog;

  const [, freezeCustomerStatus] = useMutationNotificationWrapper(
    useFreezeCustomerStatus(),
    {
      success: {
        message: "Customer Freeze Status has been changed successfully.",
      },
    }
  );

  const [, unFreezeCustomerStatus] = useMutationNotificationWrapper(
    useUnFreezeCustomerStatus(),
    {
      success: {
        message: "Customer Freeze Status has been changed successfully.",
      },
    }
  );

  const [
    showFreezeConfirmationModal,
    { open: openFreezeConfirmationModal, close: closeFreezeConfirmationModal },
  ] = useDisclosure(false);

  const isCustomerFreezed =
    clikData?.company?.freezeStatus === CustomerFreezeStatus.Active;

  const isFetching =
    clikCitiesFetching ||
    clikCountriesFetching ||
    companiesFetching ||
    clikDataFetching;

  const isCustomerDirectorInfoEmpty = !isFetching && !customerDirectorInfo;

  const displayTabs = getTabs({ isCustomerDirectorInfoEmpty, user });
  const displayTabValues = displayTabs.map((item) => item.value);
  const activeTab = useTabFromUrl({
    defaultTab: displayTabValues[0],
    tabs: displayTabValues,
  });

  const handleBack = () => navigate(AppRoute.ClikData);

  if (error || isNull(clikData)) {
    navigate(AppRoute.ClikData);
    notifications.show({
      message: "Something went wrong while trying to fetch data.",
      variant: "error",
    });
  }

  const handleChangeFreezeCustomerStatus = useCallback(
    (reason: string) => {
      if (!customerId) {
        return;
      }
      const handler = isCustomerFreezed
        ? unFreezeCustomerStatus
        : freezeCustomerStatus;
      handler({
        id: customerId,
        reason,
      }).then(() => {
        closeFreezeConfirmationModal();
        refreshClikData();
        refreshCustomerChangeLog();
      });
    },
    [
      closeFreezeConfirmationModal,
      customerId,
      freezeCustomerStatus,
      isCustomerFreezed,
      refreshClikData,
      refreshCustomerChangeLog,
      unFreezeCustomerStatus,
    ]
  );

  const freezeStatusCustomerButton = useMemo(() => {
    if (isCustomerFreezed) {
      return (
        <Button
          variant="outlineGolden"
          uppercase
          onClick={openFreezeConfirmationModal}
        >
          Unfreeze Customer
        </Button>
      );
    }
    return (
      <Button
        variant="filledGolden"
        leftIcon={<SvgSnowflake />}
        uppercase
        onClick={openFreezeConfirmationModal}
      >
        Freeze Customer
      </Button>
    );
  }, [isCustomerFreezed, openFreezeConfirmationModal]);

  return (
    <Layout>
      <Flex direction="column" gap={{ base: 24, sm: 20 }}>
        <Flex gap={20} justify="space-between">
          <Flex gap={20}>
            <ActionIcon size="m" variant="outlineGolden" onClick={handleBack}>
              <SvgChevronLeft />
            </ActionIcon>
            <Title className={classes.titleWrapper}>
              {clikData?.company?.name
                ? `Risk Management - ${clikData.company.name}`
                : "Risk Management"}
            </Title>
          </Flex>
          <Flex gap={20}>
            {freezeStatusCustomerButton}
            <CustomerStatusLog
              data={customerChangeLog}
              loading={customerChangeLogFetching}
            />
          </Flex>
        </Flex>
        <Tabs
          value={activeTab}
          variant="outline"
          keepMounted={false}
          onTabChange={(value) => navigate(`?tab=${value}`)}
        >
          <Tabs.List>
            {displayTabs.map((item, index) => {
              return (
                <Tabs.Tab key={index} value={item.value}>
                  <Flex align="center">{item.children ?? item.label}</Flex>
                </Tabs.Tab>
              );
            })}
          </Tabs.List>
          <Tabs.Panel value={TabValue.CompanyInfo}>
            <CompanyInfo
              clikDataParentId={clikDataParentId}
              customerDirectorInfo={customerDirectorInfo}
              cities={clikCitiesData?.clikCities}
              countries={clikCountriesData?.clikCountries}
              companies={companiesData?.customers.data}
              isFetching={isFetching || fetchingCustomerDirectorInfo}
            />
          </Tabs.Panel>
          <Tabs.Panel value={TabValue.PengurusInfo} mih="20vh">
            <PengurusInfo
              clikDataParentId={clikDataParentId}
              loading={isFetching}
              cities={clikCitiesData?.clikCities}
              countries={clikCountriesData?.clikCountries}
            />
          </Tabs.Panel>
          <Tabs.Panel value={TabValue.ClikResult}>
            <ClikResultList companyId={customerId} />
          </Tabs.Panel>
          <Tabs.Panel value={TabValue.LimitInfo}>
            <LimitInfo company={clikData?.company} />
          </Tabs.Panel>
          <Tabs.Panel value={TabValue.TransactionHistory}>
            <TransactionHistoryList customer={clikData?.company} />
          </Tabs.Panel>
          <Tabs.Panel value={TabValue.InvoicesHistory}>
            <InvoicesHistoryList customer={clikData?.company} />
          </Tabs.Panel>
          <Tabs.Panel value={TabValue.LateFee}>
            <LateFeeTab customerId={customerId} />
          </Tabs.Panel>
        </Tabs>
      </Flex>
      {showFreezeConfirmationModal && (
        <ModalWithNotes
          opened={true}
          title={`Are you sure you want to ${
            isCustomerFreezed ? "Unfreeze" : "Freeze"
          } this Customer?`}
          buttonTitle={isCustomerFreezed ? "Unfreeze" : "Freeze"}
          input={{
            label: `Please provide the reason for ${
              isCustomerFreezed ? "Unfreezing" : "Freezing"
            } this customer.*`,
            placeholder: "Enter Reason",
            validationError: "Reason is required",
          }}
          onSubmit={handleChangeFreezeCustomerStatus}
          onClose={closeFreezeConfirmationModal}
        />
      )}
    </Layout>
  );
};

export default ClikDataDetailsPage;
